import { ActiveType, Type } from "@/types";
import { SelectChangeEvent, MenuItem, Select } from "@mui/material";
import { useEffect, useState } from "react";

interface DiscountStatusFiltersProps {
  active: ActiveType | "";
  onActiveChange: (event: SelectChangeEvent) => void;
  type: Type | "";
  onTypeChange: (event: SelectChangeEvent) => void;
  searchTerm: string;
  onSearch?: (value: string) => void;
  onApply?: () => void;
  onReset?: () => void;
}

const DiscountStatusFilters = ({ active, onActiveChange, type, onTypeChange, searchTerm, onSearch, onApply, onReset }: DiscountStatusFiltersProps) => {
  const [openStatus, setOpenStatus] = useState(false);
  const [openType, setOpenType] = useState(false);


  useEffect(() => {
    const handleScroll = () => {
      if (openStatus) {
        setOpenStatus(false);
      }
      if (openType) {
        setOpenType(false);
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [openStatus, openType]);

  return (
    <div className="flex w-full flex-1 flex-wrap gap-3 max-sm:mt-8 max-sm:flex-col">
      <div className="flex items-center gap-3 max-md:flex-1 max-sm:flex-col">
        <div className="flex h-12 w-1/2 items-center gap-3 rounded-lg border border-[#E2E8F0] bg-white px-3 max-sm:w-full lg:min-w-[269px] lg:max-w-[269px]">
          <img
            src="/icons/search.svg"
            alt="search"
            className="aspect-square lg:size-5"
          />
          <input
            placeholder="Search Name, Code"
            onChange={(e) => onSearch?.(e.target.value)}
            inputMode="search"
            value={searchTerm}
            className="w-full bg-white text-sm font-normal text-text outline-none placeholder:text-[#94A3B8]"
          />
        </div>

        <div className="w-[269px] max-sm:w-full lg:max-w-[269px]">
          <Select
            open={openStatus}
            onOpen={() => setOpenStatus(true)}
            onClose={() => setOpenStatus(false)}
            MenuProps={{
              disableScrollLock: true,
            }}
            value={active}
            onChange={onActiveChange}
            displayEmpty
            renderValue={(selected: ActiveType | "") => {
              if (selected === "")
                return <span style={{ color: "#94A3B8" }}>All status</span>;
              if (selected === ActiveType.ALL) return ActiveType.ALL;
              return selected;
            }}
            sx={{
              cursor: "pointer",
              height: "48px",
              backgroundColor: "white",
              "& .MuiOutlinedInput-notchedOutline": {
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
              },
              "& .MuiSelect-select": {
                padding: "12px 16px",
                color: active ? "#1E293B" : "#94A3B8",
                fontSize: "14px",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "1px solid #E2E8F0",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "1px solid #E2E8F0",
              },
            }}
            IconComponent={(props) => (
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                {...props}
                style={{
                  right: 8,
                  position: "absolute",
                  pointerEvents: "none",
                }}
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="#94A3B8"
                  strokeWidth="1.67"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          >
            {Object.values(ActiveType).map((type) => (
              <MenuItem
                key={type}
                value={type}
                sx={{
                  color: "#475569",
                  fontSize: "14px",
                  padding: "12px 20px",
                }}
              >
                {type}
              </MenuItem>
            ))}
          </Select>
        </div>

        <div className="w-[269px] max-sm:w-full lg:max-w-[269px]">
          <Select
            open={openType}
            onOpen={() => setOpenType(true)}
            onClose={() => setOpenType(false)}
            MenuProps={{
              disableScrollLock: true,
            }}
            value={type}
            onChange={onTypeChange}
            displayEmpty
            renderValue={(selectedType: Type | "") => {
              if (selectedType === "")
                return <span style={{ color: "#94A3B8" }}>All types</span>;
              if (selectedType === Type.ALL) return Type.ALL;
              return selectedType;
            }}
            sx={{
              cursor: "pointer",
              height: "48px",
              backgroundColor: "white",
              "& .MuiOutlinedInput-notchedOutline": {
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
              },
              "& .MuiSelect-select": {
                padding: "12px 16px",
                color: type ? "#1E293B" : "#94A3B8",
                fontSize: "14px",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "1px solid #E2E8F0",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "1px solid #E2E8F0",
              },
            }}
            IconComponent={(props) => (
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                {...props}
                style={{
                  right: 8,
                  position: "absolute",
                  pointerEvents: "none",
                }}
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="#94A3B8"
                  strokeWidth="1.67"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          >
            {Object.values(Type).map((type) => (
              <MenuItem
                key={type}
                value={type}
                sx={{
                  color: "#475569",
                  fontSize: "14px",
                  padding: "12px 20px",
                }}
              >
                {type}
              </MenuItem>
            ))}
          </Select>
        </div>
      </div>

      <div className="flex items-center gap-3 max-md:flex-1 max-sm:hidden">
        <button
          className="h-12 w-[63px] rounded-lg bg-text text-base font-medium text-white outline-none disabled:opacity-50 max-md:text-[12px] md:w-[100px] lg:h-12"
          onClick={onApply}
        >
          Apply
        </button>

        <button
          className="h-12 w-[63px] rounded-lg bg-text text-base font-medium text-white outline-none disabled:bg-neutral-100 disabled:text-neutral-400 max-md:text-[12px] md:w-[100px] lg:h-12"
          onClick={onReset}
          disabled={!active && !type && !searchTerm}
        >
          Reset
        </button>
      </div>
    </div>
  );
};

export default DiscountStatusFilters;
